import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { getRequest, deleteRequest, tabulatorGet } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {
  AddIcon,
  EditIcon,
  CopyIcon,
  DeleteIcon,
  PrintIcon,
  SearchIcon,
  ExportIcon,
  RefreshIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  EditRowIcon,
} from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../../components/maintenance/MaintenanceMaskDialogCheckbox';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { buildNavigationContextFromTable } from '../../../utils/maintenanceNavigationContext';
import { useTabulatorScrollArrows } from '../../../utils/useTabulatorScrollArrows';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  clearHeaderFilterForField,
  normalizeColumnVisibilityForSave,
  applyDataColumnVisibilityToTable,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  applyCompanyListColumnLayout,
  restoreCompanyListUserMaskToTable,
  mergePagedStandardListFiltersAndSorters,
  TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getPusherChannel } from '../../../utils/pusherChannel';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from '../../../utils/tabulatorColumnMinWidth';
import { buildAuditTabulatorColumns, mergeColumnVisibilityWithAuditDefaults } from '../../../utils/tabulatorAuditColumns';
import { buildTabulatorIdColumn } from '../../../utils/tabulatorIdColumn';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import { OverlappingAvatarGroup } from '../../../utils/overlappingAvatarGroup';
import { LocaleContext } from '../../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../../constants/brandingDisplay';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../../utils/printChrome';
const SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'name': 'Common.Name',
  'status': 'Common.Type',
  'regularity': 'ServiceTaskDetail.Regularity',
  'custom_regularity': 'SummaryEmailOTKList.Column.RegularityDetail',
  'email_time': 'SummaryEmailOTKList.Column.EmailTime',
  'custom_other_emails': 'SummaryEmailOTKList.Column.OtherEmails',
  'rowSelection': 'Common.Name',
  'interval': 'SummaryEmailOTKList.Column.Interval',
  'custom_employees': 'SummaryEmailOTKList.Column.Employees',
});



const MASK_PAGE = 'SummaryEmailMaintenanceList';

const DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  name: true,
  status: true,
  interval: true,
  regularity: true,
  custom_regularity: true,
  email_time: true,
  custom_employees: true,
  custom_other_emails: true,
  created_at: true,
  updated_at: true,
});

const SUMMARY_EMAIL_MAINTENANCE_LIST_DATA_FIELD_ORDER = Object.freeze([
  'id',
  'name',
  'status',
  'interval',
  'regularity',
  'custom_regularity',
  'email_time',
  'custom_employees',
  'custom_other_emails',
  'created_at',
  'updated_at',
]);

const summaryEmailMaintenanceListRemoteHeaderFilterPassthrough = () => true;

function mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY, saved);
}

function applySummaryEmailMaintenanceListDataColumnVisibility(table, visibility) {
  applyDataColumnVisibilityToTable(table, DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY, visibility);
}

function parseSummaryData(rawData) {
  if (!rawData) return {};
  if (typeof rawData === 'object') return rawData;
  if (typeof rawData !== 'string') return {};
  try {
    return JSON.parse(rawData);
  } catch (_e) {
    return {};
  }
}

function matchAny(data, filterParams) {
  const regex = RegExp(filterParams.value, 'i');
  for (const key in data) {
    if (regex.test(data[key]) === true) {
      return true;
    }
  }
  return false;
}

export default function SummaryEmailMaintenanceList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getUserInitials,
    getUserColor,
    getDateFormat,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [insertIcon, setInsertIcon] = useState(!user.permissions?.includes('edit maintenance-setup'));

  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState({});
  const [searchIconColor, setSearchIconColor] = useState('');
  const [columnsVisible, setColumnsVisible] = useState(() => ({
    ...DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
  }));
  const [maskVisible, setMaskVisible] = useState(false);

  const ref = useRef(null);
  const tabulatorInstanceRef = useRef(null);
  const elementRef = useRef(null);
  const overlayRef = useRef(null);
  const tableBuiltRef = useRef(false);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const isRestoringRef = useRef(false);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 50;
  }, [state.module_settings]);

  const getTable = useCallback(
    () => tabulatorInstanceRef.current ?? ref.current?.table ?? ref.current,
    []
  );

  const buildNavContext = useCallback(
    () =>
      buildNavigationContextFromTable({
        table: getTable(),
        cleanSorters,
        extra: { type: 'maintenance' },
      }),
    [getTable]
  );

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useTabulatorScrollArrows(getTable);

  const refreshFromStart = useCallback(
    () => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingData')),
    [getTable]
  );

  const buildRequestParams = useCallback(
    (paramsFromTabulator = {}) => {
      const extra = { type: 'maintenance', ...extractSafePaginationParams(paramsFromTabulator) };
      if (extra.size == null) extra.size = pageSizePc;
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: state.user_mask_filters,
        maskPage: MASK_PAGE,
        filterKeyStyle: 'literal_field',
      });
      return extra;
    },
    [getTable, state.user_mask_filters, pageSizePc]
  );

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };
    const onCreated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('summary_email.created', id)) return;
      getRequest(`/api/summary_emails/${id}`)
        .then((res) => {
          const rowData = res?.data ?? res;
          if (!rowData?.id || rowData.type !== 'maintenance') return;
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        })
        .catch(() => {});
    };
    const onUpdated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('summary_email.updated', id)) return;
      getRequest(`/api/summary_emails/${id}`)
        .then((res) => {
          const rowData = res?.data ?? res;
          if (!rowData?.id) return;
          const table = getTable();
          const row = table?.getRow?.(rowData.id) ?? table?.getRows?.().find((r) => r.getData().id === id);
          if (rowData.type === 'maintenance') {
            if (row) row.update(rowData);
            else table?.addData?.([rowData], true);
          } else if (row) row.delete();
        })
        .catch(() => {});
    };
    const onDeleted = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('summary_email.deleted', id)) return;
      const table = getTable();
      const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
      if (row) row.delete();
    };
    channel.bind('summary_email.created', onCreated);
    channel.bind('summary_email.updated', onUpdated);
    channel.bind('summary_email.deleted', onDeleted);
    return () => {
      channel.unbind('summary_email.created', onCreated);
      channel.unbind('summary_email.updated', onUpdated);
      channel.unbind('summary_email.deleted', onDeleted);
    };
  }, [getTable]);

  // Refresh list po uložení z SummaryEmailMaintenanceAdd (mntn034) — parita GaugeCalibrationList
  useEffect(() => {
    const n = state.refreshSummaryEmailsMaintenanceList ?? 0;
    if (n === 0 || !tableBuiltRef.current) return;
    captureHScroll();
    refreshFromStart();
  }, [state.refreshSummaryEmailsMaintenanceList, captureHScroll, refreshFromStart]);

  const accept = () => {
    const rows_to_delete = getTable()?.getSelectedData?.() ?? [];
    rows_to_delete.forEach((element) => {
      deleteRequest(`/api/summary_emails/${element.id}`)
        .then(() => {
          toast?.current?.show({
            severity: 'info',
            summary: t('Common.Confirmed'),
            life: 3000,
            detail: t('Common.Toast.RecordDeletedDetail'),
          });
          refreshFromStart();
        })
        .catch(() =>
          toast?.current?.show({
            severity: 'warn',
            summary: t('Common.Rejected'),
            detail: t('Common.Toast.DeleteFailedDetail'),
            life: 3000,
          })
        );
    });
  };

  const reject = () => {
    toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.RequestCancelled'), life: 3000 });
  };

  const confirm = () => {
    confirmDialog({
      message: t('Common.ConfirmDeleteRecord'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept,
      reject,
    });
  };

  const onSelectRow = () => {
    setEditIcon(true);
    setCopyIcon(true);
    setDeleteIcon(true);
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (selected.length > 0) {
      if (selected.length === 1) {
        if (user.permissions?.includes('edit maintenance-setup')) setCopyIcon(false);
        if (user.permissions?.includes('edit maintenance-setup')) setEditIcon(false);
      }
      if (user.permissions?.includes('delete maintenance-setup')) setDeleteIcon(false);
    }
  };

  const onEditRow = useCallback(
    (summary_email) => {
      dispatch({
        type: 'create_new_tab', name: t('Common.SummaryEmail'), translationKey: 'Common.SummaryEmail', legacyLabel: t('Common.SummaryEmail'),
        page: 'SummaryEmailMaintenanceAdd',
        props: { summary_email, type: 'edit', navigationContext: buildNavContext(), not_last_used: true },
        mask: 'mntn034',
      });
    },
    [dispatch, buildNavContext]
  );

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function RegularityFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const parsed = parseSummaryData(rowData?.data);
    const period = Array.isArray(parsed?.time_period) ? parsed.time_period : [];
    if (period.length > 0) {
      return period.map((item) => item?.name).filter(Boolean).join('/');
    }
    return null;
  }

  function OtherEmailsFormatter(props) {
    const rowData = props.cell.getRow().getData();
    if (rowData.other_emails) {
      return rowData.other_emails.split(',').join('/');
    }
    return null;
  }

  function EmployeesFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const employees = Array.isArray(rowData?.employees) ? rowData.employees : [];
    return (
      <OverlappingAvatarGroup
        items={employees}
        getKey={(item) => item.id ?? item.name}
        getDisplayName={(item) => item.name ?? ''}
        getColorKey={(item) => item.name ?? ''}
        getUserInitials={getUserInitials}
        getUserColor={getUserColor}
      />
    );
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        includeId: false,
        dateFormat: dateFormatGlob,
        dateTimeFormat: dateTimeFormatGlob,
        getUserColor,
        getUserInitials,
        textHeaderFilter: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        remotePassthrough: true,
      }),
    [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials]
  );

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'rowSelection',
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        headerSort: false,
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        formatter: reactFormatter(<EditRowFormatter />),
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      buildTabulatorIdColumn({
        frozen: true,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
      }),
      {
        title: t('Common.Name'),
        field: 'name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Name')),
        widthGrow: 1,
      },
      {
        title: t('Common.Type'),
        field: 'status',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Type')),
        widthGrow: 1,
      },
      {
        title: t('SummaryEmailOTKList.Column.Interval'),
        field: 'interval',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle('Interval'),
        widthGrow: 1,
      },
      {
        title: t('ServiceTaskDetail.Regularity'),
        field: 'regularity',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceTaskDetail.Regularity')),
        widthGrow: 1,
      },
      {
        title: t('SummaryEmailOTKList.Column.RegularityDetail'),
        field: 'custom_regularity',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle(t('SummaryEmailOTKList.Column.RegularityDetail')),
        widthGrow: 1,
        formatter: reactFormatter(<RegularityFormatter />),
      },
      {
        title: t('SummaryEmailOTKList.Column.EmailTime'),
        field: 'email_time',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle(t('SummaryEmailOTKList.Column.EmailTime')),
        widthGrow: 1,
      },
      {
        title: t('SummaryEmailOTKList.Column.Employees'),
        field: 'custom_employees',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle('Zamestnanci'),
        widthGrow: 1,
        formatter: reactFormatter(<EmployeesFormatter />),
      },
      {
        title: t('SummaryEmailOTKList.Column.OtherEmails'),
        field: 'custom_other_emails',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: summaryEmailMaintenanceListRemoteHeaderFilterPassthrough,
        minWidth: tabulatorColumnMinWidthFromTitle(t('SummaryEmailOTKList.Column.OtherEmails')),
        widthGrow: 1,
        formatter: reactFormatter(<OtherEmailsFormatter />),
      },
      ...auditTailColumns,
    ],
    [onEditRow, getUserInitials, getUserColor, auditTailColumns]
  );

  const addNewSummaryEmail = () => {
    dispatch({
      type: 'create_new_tab', name: t('Common.SummaryEmail'), translationKey: 'Common.SummaryEmail', legacyLabel: t('Common.SummaryEmail'),
      page: 'SummaryEmailMaintenanceAdd',
      props: { summary_email: null, type: 'add' },
      mask: 'mntn034',
    });
  };

  const copySummaryEmail = () => {
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (selected.length > 0) {
      dispatch({
        type: 'create_new_tab', name: t('Common.SummaryEmail'), translationKey: 'Common.SummaryEmail', legacyLabel: t('Common.SummaryEmail'),
        page: 'SummaryEmailMaintenanceAdd',
        props: { summary_email: selected[0], type: 'add', navigationContext: buildNavContext(), not_last_used: true },
        mask: 'mntn034',
      });
    }
  };

  const editSummaryEmail = () => {
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (selected.length > 0) {
      dispatch({
        type: 'create_new_tab', name: t('Common.SummaryEmail'), translationKey: 'Common.SummaryEmail', legacyLabel: t('Common.SummaryEmail'),
        page: 'SummaryEmailMaintenanceAdd',
        props: { summary_email: selected[0], type: 'edit', navigationContext: buildNavContext(), not_last_used: true },
        mask: 'mntn034',
      });
    }
  };

  const searchTable = () => {
    const table = getTable();
    if (!table) return;
    setSearchIconColor('active');
    table.addFilter(matchAny, { value: searchValue });
    if (searchValue === '') {
      setSearchIconColor('');
      table.removeFilter();
    }
    setVisibleSearch(false);
  };

  const itemRenderer = (item, options) => (
    <a
      className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]"
      onClick={options.onClick}
    >
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && (
        <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>
      )}
    </a>
  );

  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const hasApplicableStandardMask = useMemo(
    () =>
      hasApplicableStandardMaskRow(
        state.standard_mask_filters,
        MASK_PAGE,
        mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved
      ),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved,
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(() => {
    const normalized = normalizeColumnVisibilityForSave(
      DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      columnsVisible
    );
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({
        severity: 'warn',
        summary: 'Maska',
        detail: t('Common.SelectAtLeastOneColumn'),
        life: 4000,
      });
      return;
    }
    const table = getTable();
    applySummaryEmailMaintenanceListDataColumnVisibility(table, normalized);
    const filtersSnapshot = table?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(MASK_PAGE, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  }, [columnsVisible, getTable, setUserMaskFilter, toast]);

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved,
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        applyCompanyListColumnLayout(table, attributes, SUMMARY_EMAIL_MAINTENANCE_LIST_DATA_FIELD_ORDER, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
        setColumnsVisible(colsForSave);
        applySummaryEmailMaintenanceListDataColumnVisibility(table, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.clearAlert?.();
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast, captureHScroll, refreshFromStart]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applySummaryEmailMaintenanceListDataColumnVisibility,
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applySummaryEmailMaintenanceListDataColumnVisibility,
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applySummaryEmailMaintenanceListDataColumnVisibility,
    }),
    [getTable]
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: buildMaintenanceMaskAkcieSubItems({
        maskPage: MASK_PAGE,
        getTable,
        setUserMaskFilter,
        cleanSorters,
        captureHScroll,
        refreshFromStart,
        closeMenu,
        openMaskDialog,
        openChosenNotificationPreferences,
        hasApplicableStandardMask,
        confirmApplyStandardLayoutToUserMask,
        itemRenderer,
      }),
    },
  ];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add">
            <button className="disabled:opacity-25" disabled={insertIcon} onClick={() => addNewSummaryEmail()} type="button">
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button className="disabled:opacity-25" disabled={editIcon} onClick={() => editSummaryEmail()} type="button">
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy">
            <button className="disabled:opacity-25" disabled={copyIcon} onClick={() => copySummaryEmail()} type="button">
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete">
            <button className="disabled:opacity-25" onClick={confirm} disabled={deleteIcon} type="button">
              <DeleteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor('');
                      setSearchValue('');
                      getTable()?.clearFilter?.();
                    }
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" title={t('Common.Search')} />
              ) : (
                <MdSearchOff fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" title={t('Common.CancelSearch')} />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={() => getTable()?.download?.('xlsx', 'data.xlsx', { sheetName: 'MyData' })}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="tofirst">
            <button type="button" onClick={() => scrollToFirst()}>
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="toup">
            <button type="button" onClick={() => scrollUp()}>
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="todown">
            <button type="button" onClick={() => scrollDown()}>
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="tolast">
            <button type="button" onClick={() => scrollToLast()}>
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog('Emaily', MASK_PAGE)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView(MASK_PAGE, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={elementRef}>
                <ReactTabulator
                  data={undefined}
                  className="h-[calc(100vh_-_193px)]"
                  onRef={(r) => {
                    ref.current = r.current;
                    tabulatorInstanceRef.current = r.current?.table ?? r.current;
                  }}
                  columns={columns}
                  events={{
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      isRestoringRef.current = true;
                      const effective = getEffectiveMask(MASK_PAGE, state);
                      if (effective?.type === 'user' && effective.userMask) {
                        const m = effective.userMask;
                        restoreCompanyListUserMaskToTable(table, m, {
                          canonicalFieldOrder: SUMMARY_EMAIL_MAINTENANCE_LIST_DATA_FIELD_ORDER,
                          forceFrozenId: true,
                          setColumnsVisible,
                          clearHeaderFiltersWhenEmpty: true,
                          applyColumnVisibilityFn: (tbl, jc, setVis) => {
                            const merged = mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved(jc);
                            setVis(merged);
                            applySummaryEmailMaintenanceListDataColumnVisibility(tbl, merged);
                          },
                        });
                        const merged = mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_SUMMARY_EMAIL_MAINTENANCE_LIST_COLUMN_VISIBILITY)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      } else if (effective?.type === 'standard' && effective.standardMask) {
                        const m = effective.standardMask;
                        const attributes = safeParse(m.attributes, []);
                        const jsonColumns = safeParse(m.columns, {});
                        applyCompanyListColumnLayout(table, attributes, SUMMARY_EMAIL_MAINTENANCE_LIST_DATA_FIELD_ORDER, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS);
                        if (jsonColumns && typeof jsonColumns === 'object') {
                          const merged = mergeSummaryEmailMaintenanceListColumnVisibilityFromSaved(jsonColumns);
                          setColumnsVisible(merged);
                          applySummaryEmailMaintenanceListDataColumnVisibility(table, merged);
                        }
                      }
                      setTimeout(() => {
                        isRestoringRef.current = false;
                      }, 0);
                      tableBuiltRef.current = true;
                    },
                    ...getTabulatorPersistLayoutEvents(MASK_PAGE, tabulatorInstanceRef, setUserMaskFilter),
                    headerFilterChanged: () => {
                      if (!isRestoringRef.current) captureHScroll();
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) captureHScroll();
                    },
                    renderComplete: () => {
                      restoreHScroll();
                    },
                  }}
                  options={{
                    layout: 'fitColumns',
                    renderHorizontal: 'virtual',
                    rowHeight: 42,
                    movableColumns: true,
                    headerSortTristate: true,
                    printAsHtml:true,printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('Print.Title.SummaryEmailMaintenance'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                    printRowRange: 'active',
                    ajaxURL: '/api/summary_emails/paged',
                    ajaxParams: () => ({}),
                    ajaxRequestFunc: createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
                    ajaxRequesting: () => {
                      if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                      return true;
                    },
                    ajaxResponse: (_url, _params, response) => {
                      getTable()?.clearAlert?.();
                      return response;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                    dataLoader: true,
                    dataLoaderLoading: t('Common.LoadingData'),
                    dataLoaderError: t('Common.LoadFailed'),
                    placeholder: t('Common.NoData'),
                    progressiveLoad: 'scroll',
                    progressiveLoadScrollMargin: 80,
                    paginationSize: pageSizePc,
                    filterMode: 'remote',
                    sortMode: 'remote',
                    pagination: false,
                    initialSort: [{ column: 'id', dir: 'desc' }],
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button
            type="button"
            onClick={() => searchTable()}
            className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"
          >
            <SearchIcon />
          </button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`sem-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                <label htmlFor={`sem-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}
                </label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
